import React, { useEffect, useState } from "react";
import { Loader } from "@vds/loaders";
import { Icon } from "@vds/icons";
import { ButtonIcon } from "@vds/button-icons";
import DisplayCanvas from "../../polygonLib/displayPolygonLib/displayCanvas";
import { base64ToBlobUrl, ImageDownloadMedia } from "../../model/mediaService";
import { deployedRoiList, getFovSnapshot } from "../../global/utility/Utils";
import { fetchRegionsOfInterestByTag } from "../../api/DeviceServiceManager";
import { listRoi } from "../../api/RoiServiceManager";
import { transformPolygonData } from "../../global/utility/roiUtils";
import { Body } from "@vds/typography";
import { useSelector } from "react-redux";
import { isEntryExitApp } from "../../global/utility/cvAppUtils";
import { LightText } from "../../features/camerOnboarding/discoveredCameraOnboarding/styles";

const RoiImage = ({
  ruleList,
  activeSession,
  fovList,
  getRoiList,
  setAggregateAdvanceConfigList = () => {},
  setAggreagateAdvanceConfigLoadState = () => {},
  deploymentsList,
}) => {
  const cvAppList = useSelector((state) => state.cvAppReducer.cvAppList) || [];
  const pairedImages =
    useSelector((state) => state.imagesReducer.streamImages) || [];
  const roiList = useSelector((state) => state.deviceReducer.roiList) || [];
  const [cvAppRois, setCvAppRois] = useState([]);
  const [regionsOfInterest, setRegionsOfInterest] = useState([]);
  const [commonIds, setCommonIds] = useState([]);
  const [imageData, setImageData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retryingIds, setRetryingIds] = useState([]);
  const imageSize = { width: 168.23, height: 108 };
  const [errorMessage, setErrorMessage] = useState(null);
  const isEntryExit = isEntryExitApp(cvAppList, ruleList?.serviceid);

  const fetchImageForItem = async (item) => {
    const fovid = item?.parentdeviceid;
    const pairedImage =
      pairedImages && pairedImages?.find((img) => img?.id === fovid);
    if (pairedImage) {
      return pairedImage?.imageUrl;
    }
    return await ImageDownloadMedia(getFovSnapshot(fovList, fovid), activeSession, '', "snapshot");
  };

  const retryImage = async (dataId) => {
    const item = commonIds?.find((c) => c.id === dataId);
    if (!item) return;
    setRetryingIds((prev) => [...prev, dataId]);
    let url = null;
    try {
      url = await fetchImageForItem(item);
    } catch (error) {
      url = null;
      console.error("Error downloading image:", error);
    }
    setImageData((prev) =>
      prev.map((d) => (d.id === dataId ? { ...d, url } : d))
    );
    setRetryingIds((prev) => prev.filter((id) => id !== dataId));
  };

  useEffect(() => {
    const fetchData = async () => {
      if (
        (ruleList?.aggregateRule && ruleList?.aggregateRule === true) ||
        isEntryExit == true
      ) {
        if (ruleList?.serviceid) {
          const payload = {
            accountname: activeSession?.accountId,
            cvappidentifiers: [
              {
                id: ruleList?.serviceid,
              },
            ],
            filter: {
              $expand: "fields,cvapp,deploy",
            },
          };

          try {
            const roiListData = await listRoi(payload, activeSession);
            const filteredRoiList = deployedRoiList(roiList, roiListData);
            setCvAppRois(filteredRoiList);

            const regionsOfInterestData = await fetchRegionsOfInterestByTag(
              activeSession,
              ruleList?.tagid
            );
            setRegionsOfInterest(regionsOfInterestData);
            if (regionsOfInterestData && regionsOfInterestData?.length === 0) {
              setErrorMessage(`This Regions of interest no longer exists.`);
            }

            // Find common IDs
            const common = filteredRoiList?.filter((item) =>
              regionsOfInterestData.some((region) => region?.id === item?.id)
            );

            setCommonIds(common);
            if (getRoiList) {
              // Extract deviceIds and foreignIds
              const deviceIds = common?.map((item) => item && item?.id);
              const foreignIds = common?.map((item) => item && item?.foreignid);

              getRoiList(common, deviceIds, foreignIds);
            }
            // Fetch images for all common IDs
            const imagePromises = common?.map(async (item) => {
              let url = null;
              try {
                url = await fetchImageForItem(item);
              } catch (error) {
                url = null;
                console.error("Error downloading image:", error);
              }
              const aggregateDeployment =
                deploymentsList &&
                deploymentsList?.find((f) => f?.id === item?.id);
              return {
                id: item.id,
                url: url,
                roiPolygon: transformPolygonData(item?.customdata),
                maxX: item.customdata,
                maxY: item.customdata,
                deployments: item?.deployments
                  ? item?.deployments
                  : aggregateDeployment?.deployments,
                name: item?.name,
              };
            });

            const imageData = await Promise.all(imagePromises);
            setImageData(imageData);
            setAggregateAdvanceConfigList(imageData);
          } catch (e) {
            setErrorMessage(
              `An error occured while trying to fetch regions of interest.Please try again later.`
            );
            console.log("Error fetching data", e);
          } finally {
            setLoading(false);
            setAggreagateAdvanceConfigLoadState(false);
          }
        }
      } else {
        setLoading(false);
        setAggreagateAdvanceConfigLoadState(false);
      }
    };

    fetchData();
  }, [ruleList?.serviceid, activeSession, fovList]);

  if (errorMessage) {
    return (
      <Body color="#EE0000" bold={true} size="large">
        {errorMessage}
      </Body>
    );
  }

  return (
    <>
      <div className="roi-image-block">
        {loading ? (
          <div className="loader-absolute-container">
            <Loader active={loading} fullscreen={false} surface="light" />
          </div>
        ) : (
          ""
        )}
        {imageData.map((data) =>
          retryingIds.includes(data.id) ? (
            <div className="alert-snapshot-loading" key={data.id}>
              <div className="camera-snapshot-loader-wrapper">
                <Loader active={true} fullscreen={false} surface="light" />
              </div>
            </div>
          ) : !data?.url ? (
            <div className="alert-snapshot-error" key={data.id}>
              <div className="camera-snapshot-error-content">
                <Icon name="error" size="small" color="#6f7171" />
                <LightText>Snapshot failed to load</LightText>
                <ButtonIcon onClick={() => retryImage(data.id)} renderIcon={() => <Icon name="sync" size="small" />} />
              </div>
            </div>
          ) : (
            <DisplayCanvas key={data.id} videoSource={data.url} imageSize={imageSize} initialPolygons={data.roiPolygon} originalSize={[data.maxX, data.maxY]} />
          )
        )}
      </div>
    </>
  );
};

export default RoiImage;
